import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DecisionLogic } from "@/components/DecisionLogic";
import { Markdown } from "@/components/Markdown";
import { SettingsChatPanel } from "@/components/SettingsChatPanel";
import { SplitPane } from "@/components/SplitPane";
import { ChangeLogPanel } from "@/pages/PracticeSummary";
import { api } from "@/lib/api";
import { type SystemPrompt } from "@/lib/types";

/** The conversation on this page is about the SHARED configuration, not any one practice's. */
const ADMIN_SCOPE = { kind: "admin" } as const;

/** Known languages the system prompts can be edited in (ISO 639-1 → Czech label). Mirrors the per-practice
 *  `language.allowed` list and the post-call `languageName` map. Czech is always first / the source language. */
const LANGUAGES: { code: string; label: string }[] = [
  { code: "cs", label: "Čeština" },
  { code: "sk", label: "Slovenština" },
  { code: "pl", label: "Polština" },
  { code: "en", label: "Angličtina" },
  { code: "uk", label: "Ukrajinština" },
  { code: "de", label: "Němčina" },
  { code: "hu", label: "Maďarština" },
  { code: "ru", label: "Ruština" },
  { code: "vi", label: "Vietnamština" },
];

const langLabel = (code: string) =>
  LANGUAGES.find((l) => l.code === code)?.label ?? code.toUpperCase();

/** Editor for the system-wide prompts (shared text per language, rendered per practice using each practice's voice).
 *  Czech is the source language; other languages are translations. A practice renders these in its own
 *  `practice.language`, falling back to Czech for any prompt not yet translated.
 *  Any logged-in `@young.co.cz` user can edit these — no role system yet per the current scope decision.
 */
export const AdminSystemPrompts = () => (
  // The page frame lives here because this is a top-level route with no layout around it, unlike the practice pages
  // which get theirs from `PracticeDetailLayout`. `max-w-7xl` rather than the `5xl` this page used before the split:
  // two panes at the old width leave the assistant too narrow to read a proposal in, which is what the practice
  // Souhrn page widens to for the same reason.
  <div className="mx-auto max-w-7xl p-8">
    <SplitPane
      storageKey="hlasemmy.admin.chatWidth"
      left={<AdminSystemPromptsBody />}
      right={<SettingsChatPanel scope={ADMIN_SCOPE} />}
    />
  </div>
);

/** Everything that is not the assistant. Split out so the page can be a two-pane layout without indenting four
 *  hundred lines. */
const AdminSystemPromptsBody = () => {
  const query = useQuery({
    queryKey: ["system-prompts"],
    queryFn: api.listSystemPrompts,
  });

  const renderAll = useMutation({
    mutationFn: () => api.renderAllPractices(),
  });

  const qc = useQueryClient();

  const practices = useQuery({
    queryKey: ["practices"],
    queryFn: api.listPractices,
  });
  const practiceFulls = useQuery({
    queryKey: ["practice-languages"],
    enabled: (practices.data?.length ?? 0) > 0,
    queryFn: async () =>
      Promise.all((practices.data ?? []).map((p) => api.getPractice(p.id))),
  });
  const languagesInUse = useMemo(() => {
    const set = new Set<string>();
    for (const p of practiceFulls.data ?? [])
      for (const l of p.languages ?? []) set.add(l.language);
    return set;
  }, [practiceFulls.data]);

  const translate = useMutation({
    mutationFn: (language: string) => api.translateSystemPrompts(language),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["system-prompts"] }),
  });

  const rows = useMemo(() => query.data ?? [], [query.data]);

  const csSlots = useMemo(
    () =>
      rows
        .filter((r) => r.language === "cs")
        .sort((a, b) => a.displayOrder - b.displayOrder),
    [rows],
  );

  const byLang = useMemo(() => {
    const m = new Map<string, Map<string, SystemPrompt>>();
    for (const r of rows) {
      if (!m.has(r.language)) m.set(r.language, new Map());
      m.get(r.language)!.set(r.promptId, r);
    }
    return m;
  }, [rows]);

  const [extraLangs, setExtraLangs] = useState<string[]>([]);
  const presentLangs = useMemo(() => {
    const set = new Set<string>([...byLang.keys(), ...extraLangs]);
    set.add("cs");
    return [...set].sort((a, b) => {
      const ia = LANGUAGES.findIndex((l) => l.code === a);
      const ib = LANGUAGES.findIndex((l) => l.code === b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
  }, [byLang, extraLangs]);

  const [activeLang, setActiveLang] = useState("cs");

  const addableLangs = LANGUAGES.filter((l) => !presentLangs.includes(l.code));

  return (
    <div className="space-y-6">
      <AdminSummaryPanel />

      <div className="mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-2xl tracking-tight text-[#1a2b6b]">
            Systémové zprávy
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Sdílený text používaný v průběhu hovoru u všech ordinací, v
            jednotlivých jazycích. Každá ordinace přehrává tyto zprávy ve svém
            jazyce (chybějící překlad se doplní češtinou). Úpravy zde změní
            audio u <em>všech</em> ordinací — po uložení klikněte na{" "}
            <strong>Přegenerovat pro všechny ordinace</strong>.
          </p>
        </div>
        <RenderAllButton mutation={renderAll} />
      </div>

      {query.isPending && <p className="text-gray-500">Načítám…</p>}
      {query.isError && (
        <p className="text-red-600">Chyba: {(query.error as Error).message}</p>
      )}
      {query.isSuccess && (
        <>
          <div className="mb-5 flex flex-wrap items-center gap-2 border-b border-gray-200">
            {presentLangs.map((code) => (
              <button
                key={code}
                onClick={() => setActiveLang(code)}
                className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
                  activeLang === code
                    ? "border-[#1a2b6b] text-[#1a2b6b]"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {langLabel(code)}
                {code === "cs" && (
                  <span className="ml-1 text-[10px] uppercase text-gray-400">
                    zdroj
                  </span>
                )}
                {code !== "cs" && languagesInUse.has(code) && (
                  <span
                    title="Tento jazyk některá ordinace nabízí volajícím. Chybějící překlad se přehraje česky."
                    className="ml-1 rounded bg-emerald-100 px-1 py-0.5 text-[10px] font-medium text-emerald-700"
                  >
                    v provozu
                  </span>
                )}
              </button>
            ))}
            {addableLangs.length > 0 && (
              <select
                value=""
                onChange={(e) => {
                  const code = e.target.value;
                  if (!code) return;
                  setExtraLangs((prev) => [...prev, code]);
                  setActiveLang(code);
                }}
                className="ml-2 rounded border border-dashed border-gray-300 px-2 py-1 text-sm text-gray-500"
              >
                <option value="">+ Přidat jazyk</option>
                {addableLangs.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          {activeLang !== "cs" && (
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <button
                onClick={() => translate.mutate(activeLang)}
                disabled={translate.isPending}
                className="rounded border border-[#1a2b6b] px-3 py-1.5 text-xs font-medium text-[#1a2b6b] hover:bg-[#1a2b6b] hover:text-white disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
              >
                {translate.isPending ? "Překládám…" : "Přeložit vše z češtiny"}
              </button>
              <span className="text-xs text-gray-500">
                Strojový překlad přepíše i texty, které jste tu už ručně upravili.
                Po kontrole nezapomeňte přegenerovat.
              </span>
              {translate.isError && (
                <span className="text-xs text-red-600">
                  {(translate.error as Error).message}
                </span>
              )}
            </div>
          )}

          <div className="space-y-4">
            {csSlots.map((slot) => {
              const row = byLang.get(activeLang)?.get(slot.promptId);
              return (
                <SystemPromptRow
                  key={`${activeLang}-${slot.promptId}`}
                  promptId={slot.promptId}
                  language={activeLang}
                  description={slot.description}
                  storedText={row?.czechText}
                  csReference={slot.czechText}
                  updatedAt={row?.updatedAt}
                />
              );
            })}
          </div>
        </>
      )}

      {/* The other half of what a language needs. The lines above are what Emmy SAYS in a language; below is what it
          understands when the caller answers. A language could be perfectly translated here and still misread every
          yes, which is exactly the state English and Hungarian were in. */}
      <div className="mt-12 border-t border-gray-200 pt-8">
        <h2 className="mb-1 text-2xl tracking-tight text-[#1a2b6b]">
          Rozhodování v hovoru
        </h2>
        <p className="mb-6 text-sm text-gray-500">
          Co Emmy udělá s odpovědí volajícího — a která slova v jednotlivých
          jazycích rozpozná jako ano a ne. Platí pro všechny ordinace najednou.
        </p>
        <DecisionLogic />
      </div>

      <ChangeLogPanel scope={ADMIN_SCOPE} />
    </div>
  );
};

/** What the shared configuration currently is, in prose. The sibling of a practice's Souhrn, generated the same way
 *  and stale-checked the same way — and, like it, not editable here: changes are made by asking the assistant. */
const AdminSummaryPanel = () => {
  const query = useQuery({
    queryKey: ["admin-summary"],
    queryFn: api.getAdminSummary,
  });

  return (
    <section className="rounded border border-gray-200 bg-white p-6">
      <h2 className="mb-1 text-2xl tracking-tight text-[#1a2b6b]">Souhrn</h2>
      <p className="mb-4 text-sm text-gray-500">
        Co je právě nastavené pro všechny ordinace dohromady. Text se
        nedá upravovat přímo — změny se dělají dotazem asistentce vpravo.
      </p>

      {query.isPending && <p className="text-sm text-gray-500">Načítám…</p>}
      {query.isError && (
        <p className="text-sm text-red-600">
          Přehled se nepodařilo načíst ({(query.error as Error).message})
        </p>
      )}
      {query.data?.stale && (
        <p className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Tento popis se právě aktualizuje — může být o krok pozadu za posledními
          změnami.
        </p>
      )}
      {query.data?.markdown && <Markdown source={query.data.markdown} />}
    </section>
  );
};

const SystemPromptRow = ({
  promptId,
  language,
  description,
  storedText,
  csReference,
  updatedAt,
}: {
  promptId: string;
  language: string;
  description?: string;
  storedText?: string; // text stored for this (promptId, language), or undefined if not translated yet
  csReference: string; // the Czech text, shown as a reference when translating
  updatedAt?: string;
}) => {
  const qc = useQueryClient();
  const translated = storedText !== undefined;
  // For an untranslated slot we pre-fill the Czech text as a starting point.
  const initial = storedText ?? csReference;
  const [text, setText] = useState(initial);
  useEffect(() => setText(initial), [initial]);

  const mutation = useMutation({
    mutationFn: () => api.updateSystemPrompt(promptId, language, text),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["system-prompts"] }),
  });

  const dirty = text !== initial;
  const showCsReference = language !== "cs";

  return (
    <section className="rounded border border-gray-200 bg-white p-6">
      <div className="mb-3 flex items-baseline justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-[#1a2b6b]">
            {promptId}
            {showCsReference && !translated && (
              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                nepřeloženo
              </span>
            )}
          </h3>
          {description && (
            <p className="text-xs text-gray-500">{description}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {mutation.isError && (
            <span className="text-xs text-red-600">
              {(mutation.error as Error).message}
            </span>
          )}
          <button
            onClick={() => mutation.mutate()}
            disabled={!dirty || mutation.isPending}
            className="rounded bg-[#1a2b6b] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0f1d52] disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {mutation.isPending ? "Ukládám…" : dirty ? "Uložit" : "Uloženo"}
          </button>
        </div>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#1a2b6b] focus:outline-none"
      />
      {showCsReference && (
        <p className="mt-2 text-[11px] text-gray-400">🇨🇿 {csReference}</p>
      )}
      {updatedAt && (
        <p className="mt-1 text-[11px] text-gray-400">
          Aktualizováno {new Date(updatedAt).toLocaleString("cs-CZ")}
        </p>
      )}
    </section>
  );
};

type RenderAllMutation = {
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: unknown;
  data?: { queued: string[]; failed: { practiceId: string; error: string }[] };
  mutate: () => void;
};

const RenderAllButton = ({ mutation }: { mutation: RenderAllMutation }) => (
  <div className="flex flex-col items-end gap-1">
    <button
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className="rounded bg-[#1a2b6b] px-4 py-2 text-sm font-medium text-white hover:bg-[#0f1d52] disabled:bg-gray-300"
    >
      {mutation.isPending ? "Zařazuji…" : "↻ Přegenerovat pro všechny ordinace"}
    </button>
    {mutation.isSuccess && mutation.data && (
      <span className="text-xs text-gray-500">
        Zařazeno: {mutation.data.queued.length} ordinac
        {mutation.data.queued.length === 1
          ? "e"
          : mutation.data.queued.length < 5
            ? "e"
            : "í"}
        {mutation.data.failed.length > 0 &&
          ` · ${mutation.data.failed.length} selhalo`}
      </span>
    )}
    {mutation.isError && (
      <span className="text-xs text-red-600">
        {(mutation.error as Error).message}
      </span>
    )}
  </div>
);
